<template>
  <form class="login-form" @submit.prevent="onSubmit" novalidate>
    <EmailFormInput
      ref="email"
      id="email"
      :value.sync="email"
      :label="$t('global.email_address')"
      :showMessageOn="showMessageOn"
      :isDisabled="isSubmitting"
      v-data-test-id="'login-email'"
      @isValid="isEmailValid = $event"
      @focus="scrollIntoViewport"
    ></EmailFormInput>

    <FormInput
      ref="password"
      id="password"
      type="password"
      :value.sync="password"
      :label="$t('checkout.login.password')"
      autocomplete="off"
      :validations="[{ validator: isNotEmpty, message: $t('errors.required_field') }]"
      :showMessageOn="showMessageOn"
      :isDisabled="isSubmitting"
      v-data-test-id="'login-password'"
      @isValid="isPasswordValid = $event"
      @focus="scrollIntoViewport"
    ></FormInput>

    <div class="login-form__row">
      <Checkbox
        ref="rememberMe"
        id="rememberMe"
        :value.sync="rememberMe"
        :label="$t('checkout.login.remember_me')"
        class="login-form__remember-me"
        :isDisabled="isSubmitting"
      ></Checkbox>
      <a
        v-if="resetPasswordUrl"
        class="login-form__reset-password"
        :class="{ 'login-form__reset-password--disabled': isSubmitting }"
        :href="resetPasswordUrl"
      >
        {{ $t(`checkout.login.${isMqMedium ? 'forgot_password' : 'forgot_short'}`) }}
      </a>
    </div>

    <Button
      :isDisabled="!isFormValid"
      :isSubmitting="isSubmitting"
      type="submit"
      class="login-form__submit"
      v-data-test-id="'login-submit'"
      >{{ loginBtnText || $t('checkout.login.login') }}</Button
    >
  </form>
</template>

<script>
import { mapGetters } from 'vuex'
import to from 'await-to-js'
import Button from '@/components/_shared/Button.vue'
import { scrollIntoViewport } from '@/components/_shared/Form/form-helper'
import FormInput from '@/components/_shared/Form/Input/FormInput.vue'
import EmailFormInput from '@/components/_shared/Form/Input/EmailFormInput.vue'
import Checkbox from '@/components/_shared/Form/Checkbox/Checkbox.vue'
import { EVENT_TYPES } from '@/components/_shared/Form/Validation/FieldValidator.vue'
import { TYPES as VALIDATION_TYPES } from '@/components/_shared/Form/Validation/FieldValidation.vue'

export default {
  name: 'LoginForm',
  components: {
    Button,
    FormInput,
    EmailFormInput,
    Checkbox,
  },

  props: {
    handleSubmit: {
      type: Function,
      required: true,
    },
    defaultEmail: {
      type: String,
      default: '',
    },
    defaultPassword: {
      type: String,
      default: '',
    },
    loginBtnText: {
      type: String,
    },
    resetPasswordUrl: {
      type: String,
    },
  },

  data() {
    return {
      email: this.defaultEmail,
      password: this.defaultPassword,
      rememberMe: false,
      isEmailValid: false,
      isPasswordValid: false,
      showMessageOn: {
        [EVENT_TYPES.blur]: [VALIDATION_TYPES.error, VALIDATION_TYPES.warning],
      },
      isSubmitting: false,
    }
  },

  computed: {
    ...mapGetters({
      isMqMedium: 'viewport/isMqMedium',
    }),

    isFormValid() {
      return this.isEmailValid && this.isPasswordValid
    },
  },

  watch: {
    isSubmitting: {
      handler() {
        this.$emit('isSubmitting', this.isSubmitting)
      },
      immediate: true,
    },

    isEmailValid: {
      handler() {
        this.$emit('isEmailValid', this.isEmailValid)
      },
      immediate: true,
    },

    isPasswordValid: {
      handler() {
        this.$emit('isPasswordValid', this.isPasswordValid)
      },
      immediate: true,
    },
  },

  mounted() {
    if (this.defaultEmail) {
      this.$refs.password.focus()
    }
  },

  methods: {
    getFormData() {
      return {
        email: this.email,
        password: this.password,
        rememberMe: this.rememberMe,
      }
    },

    async onSubmit() {
      if (this.isFormValid) {
        this.isSubmitting = true
        await to(this.handleSubmit(this.getFormData()))
        this.isSubmitting = false
      }
    },

    setError({ email, password }) {
      const type = VALIDATION_TYPES.error
      if (email) this.$refs.email.setForceMessage({ message: email, type })
      if (password) this.$refs.password.setForceMessage({ message: password, type })
    },

    isNotEmpty(value) {
      return !!value
    },

    async scrollIntoViewport({ currentTarget }) {
      scrollIntoViewport(currentTarget)
    },
  },
}
</script>

<style scoped lang="scss">
.login-form {
  &__row {
    display: flex;
    justify-content: space-between;
  }

  &__remember-me {
    margin-top: space(2);
    font-size: $font-size-small;
    line-height: 1.571;
    letter-spacing: $font-spacing-small;
    color: $color-grey-dark-2;
  }

  &__reset-password {
    margin-top: 10px;
    font-size: $font-size-smaller;
    line-height: 1.5;
    letter-spacing: $font-spacing-small;
    color: $color-grey-mid-2;

    &--disabled {
      pointer-events: none;
    }
  }

  &__submit {
    margin-top: space(9);
  }
}
</style>
